import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, Award } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuote } from "@/context/QuoteContext";
import { CompanyWebsiteLink } from "@/components/CompanyWebsiteLink";

const links = [
  { id: "manifesto", label: "Manifesto", route: "/" },
  { id: "exterior", label: "Exterior", route: "/" },
  { id: "interior", label: "Interior", route: "/" },
  { id: "garage", label: "Garage", route: "/" },
  { id: "finishes", label: "Finishes", route: "/" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  const { cart, openCartDrawer } = useQuote();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which section is currently in view for the anchor nav
  useEffect(() => {
    const compute = () => {
      const center = window.innerHeight / 2;
      let current = "";
      for (const l of links) {
        const el = document.getElementById(l.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= center && rect.bottom >= center) {
          current = l.id;
          break;
        }
      }
      setActiveId(current);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, []);

  const scrollTo = (link) => {
    if (link.external) {
      navigate(link.route);
      setOpen(false);
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${link.id}`);
      setOpen(false);
      return;
    }
    const el = document.getElementById(link.id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 inset-x-0 z-50"
        data-testid="site-navbar"
      >
        {/* Always-on gradient backdrop for legibility, tightens on scroll */}
        <div
          className={`absolute inset-0 pointer-events-none transition-all duration-500 backdrop-blur-md ${
            scrolled ? "border-b border-brand-gold/25 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.7)]" : ""
          }`}
          style={{
            background: scrolled
              ? "rgba(14, 46, 40, 0.96)"
              : "linear-gradient(to bottom, rgba(14, 46, 40, 0.88) 0%, rgba(14, 46, 40, 0.6) 70%, rgba(14, 46, 40, 0.35) 100%)",
          }}
        />
        <nav className="relative max-w-[1440px] mx-auto flex items-center justify-between gap-4 px-6 md:px-10 h-20 lg:h-24">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname !== "/") {
                navigate("/");
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="flex items-center group"
            data-testid="navbar-logo"
            aria-label="Atelier Door Collection · Home"
          >
            <img
              src="/atelier-logo.png"
              alt="Atelier Door Collection by ViewLux"
              className="h-11 md:h-14 w-auto transition-transform duration-500 group-hover:scale-[1.03]"
              draggable={false}
            />
          </a>

          <ul className="hidden min-[1440px]:flex items-center gap-5 2xl:gap-7">
            {links.map((l) => {
              const isActive = activeId === l.id;
              return (
                <li key={l.id}>
                  <button
                    onClick={() => scrollTo(l)}
                    aria-current={isActive ? "true" : undefined}
                    data-active={isActive ? "true" : "false"}
                    className={`relative text-[0.75rem] tracking-[0.28em] uppercase transition-colors font-medium after:absolute after:left-0 after:-bottom-1.5 after:h-px after:transition-all after:duration-300 ${
                      isActive
                        ? "text-brand-gold after:w-full after:bg-brand-gold"
                        : "text-brand-bone hover:text-brand-gold after:w-0 after:bg-brand-gold hover:after:w-full"
                    }`}
                    data-testid={`nav-link-${l.id}`}
                    style={{ textShadow: "0 1px 8px rgba(8, 32, 25, 0.55)" }}
                  >
                    {l.label}
                  </button>
                </li>
              );
            })}
            <li>
              <button
                onClick={() => navigate("/professional")}
                className="relative inline-flex items-center gap-1.5 text-[0.75rem] tracking-[0.28em] uppercase font-medium text-brand-gold hover:text-brand-bone transition-colors"
                data-testid="nav-pro-pricing"
                style={{ textShadow: "0 1px 8px rgba(8, 32, 25, 0.55)" }}
              >
                <Award size={13} />
                Professional Pricing
              </button>
            </li>
            <li>
              <CompanyWebsiteLink
                placement="main_menu"
                testId="nav-main-company-link"
                className="px-4 py-2 text-xs font-medium text-center leading-snug w-44"
              />
            </li>
          </ul>

          <div className="flex items-center gap-3">
            {cart.length > 0 && (
              <button
                onClick={openCartDrawer}
                className="relative inline-flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 bg-brand-gold text-brand-green text-[0.8rem] md:text-[0.9rem] tracking-[0.22em] uppercase rounded-full font-semibold hover:bg-brand-bone transition-colors shadow-lg animate-cart-pulse"
                data-testid="nav-cart"
                aria-label="Open cart"
              >
                {/* Flashing gold ring underneath — makes the pill impossible to miss */}
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-brand-gold/60 animate-ping"
                />
                <ShoppingBag size={18} className="relative" />
                <span
                  data-testid="nav-cart-count"
                  className="relative tabular-nums font-bold"
                >
                  {cart.length}
                </span>
              </button>
            )}
            <button
              onClick={openCartDrawer}
              className="inline-flex items-center gap-1.5 md:gap-2 px-3 py-1.5 md:px-6 md:py-3 bg-brand-gold text-brand-green rounded-full text-[0.6rem] md:text-[0.72rem] tracking-[0.14em] uppercase font-medium hover:bg-brand-bone transition"
              data-testid="nav-request-quote"
            >
              <span className="hidden sm:inline">Request Quote</span>
              <span className="sm:hidden">Quote</span>
              <span aria-hidden>→</span>
            </button>
            <button
              onClick={() => setOpen((o) => !o)}
              className="min-[1440px]:hidden text-brand-bone p-2 border border-brand-bone/30 rounded-full"
              data-testid="nav-mobile-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-brand-green min-[1440px]:hidden overflow-y-auto pt-28 pb-8 px-8"
            id="mobile-menu"
            data-lenis-prevent
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.li
                  key={l.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <button
                    onClick={() => scrollTo(l)}
                    className="font-serif text-4xl text-brand-bone hover:text-brand-gold"
                    data-testid={`mobile-nav-${l.id}`}
                  >
                    {l.label}
                  </button>
                </motion.li>
              ))}
              <motion.li initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}>
                <button
                  onClick={() => { setOpen(false); navigate("/professional"); }}
                  className="inline-flex items-center gap-2 text-brand-gold text-lg tracking-[0.24em] uppercase hover:text-brand-bone"
                  data-testid="mobile-nav-pro-pricing"
                >
                  <Award size={16} /> Professional Pricing
                </button>
              </motion.li>
              <motion.li initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <CompanyWebsiteLink
                  placement="main_menu"
                  testId="mobile-nav-main-company-link"
                  onClick={() => setOpen(false)}
                  className="px-5 py-3 text-sm font-medium"
                />
              </motion.li>
              <motion.li initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}>
                <button onClick={() => { setOpen(false); openCartDrawer(); }} className="btn-primary mt-4" data-testid="mobile-nav-request">
                  Request Quote →
                </button>
              </motion.li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
